// src/SetPassword.js
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, TextField, Button, Typography, Alert, Link as MuiLink } from '@mui/material';
import authProvider from './authProvider';
import { setPassword } from './api';

const SetPassword = () => {
    const [searchParams] = useSearchParams();
    const uid = searchParams.get('uid');
    const token = searchParams.get('token');
    const navigate = useNavigate();
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');

    // Redirect to login after 5 seconds if link invalid
    useEffect(() => {
        if (!uid || !token) {
            const timer = setTimeout(() => navigate('/login'), 5000);
            return () => clearTimeout(timer);
        }
    }, [uid, token, navigate]);

    // If URL params missing, show error page
    if (!uid || !token) {
        return (
            <Box 
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    paddingTop: '5vh',
                    backgroundImage: 'linear-gradient(135deg, #1a237e 0%, #121858 100%)',
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        maxWidth: '400px',
                        backgroundColor: '#fff',
                        padding: '40px',
                        borderRadius: '8px',
                        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <Box
                        component="img"
                        src="/logo.svg"
                        alt="Polfund Logo"
                        sx={{
                            width: 260,
                            marginBottom: 4,
                        }}
                    />
                    <Typography variant="h6" color="error" sx={{ textAlign: 'center' }}>
                        Nieprawidłowy lub wygasły link do resetowania hasła. Przekierowywanie do strony logowania...
                    </Typography>
                </Box>
            </Box>
        );
    }

    const handleSubmit = async event => {
        event.preventDefault();
        // Validate non-empty
        if (!newPassword || !confirmPassword) {
            setError('Hasło nie może być puste');
            return;
        }
        // Validate matching
        if (newPassword !== confirmPassword) {
            setError('Hasła nie są takie same');
            return;
        }
        setError('');
        try {
            await setPassword({ uid, token, new_password: newPassword });
            await authProvider.logout();
            navigate('/login');
        } catch (err) {
            // Handle validation errors from backend
            if (err.new_password && Array.isArray(err.new_password)) {
                setError(err.new_password.join(' '));
            } else if (err.detail) {
                // Handle detail errors (e.g., invalid link)
                if (typeof err.detail === 'string') {
                    setError(err.detail);
                } else if (err.detail.new_password) {
                    setError(Array.isArray(err.detail.new_password) 
                        ? err.detail.new_password.join(' ') 
                        : err.detail.new_password);
                } else {
                    setError('Nie udało się ustawić hasła');
                }
            } else if (err.message) {
                setError(err.message);
            } else {
                setError('Nie udało się ustawić hasła');
            }
        }
    };

    return (
        <Box 
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                paddingTop: '5vh',
                backgroundImage: 'linear-gradient(135deg, #1a237e 0%, #121858 100%)',
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    maxWidth: '400px',
                    backgroundColor: '#fff',
                    padding: '40px',
                    borderRadius: '8px',
                    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Box
                    component="img"
                    src="/logo.svg"
                    alt="Polfund Logo"
                    sx={{
                        width: 260,
                        marginBottom: 4,
                    }}
                />
                
                <Typography variant="h6" sx={{ mb: 2, color: '#333333' }}>
                    Ustaw nowe hasło
                </Typography>

                <Typography variant="body2" sx={{ mb: 2, color: '#666666', textAlign: 'center' }}>
                    Wprowadź nowe hasło i potwierdź je poniżej.
                </Typography>

                <Box sx={{ 
                    mb: 2, 
                    p: 2, 
                    bgcolor: '#f5f5f5', 
                    borderRadius: 1, 
                    width: '100%',
                    border: '2px solid #E31837'
                }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', fontSize: '0.875rem', color: '#E31837' }}>
                        Wymagania dotyczące hasła:
                    </Typography>
                    <Typography variant="body2" component="ul" sx={{ m: 0, pl: 2, fontSize: '0.75rem', color: '#333333' }}>
                        <li>Minimum 12 znaków</li>
                        <li>Wielka litera (A-Z)</li>
                        <li>Mała litera (a-z)</li>
                        <li>Cyfra (0-9)</li>
                        <li>Znak specjalny (!@#$%^&* itd.)</li>
                        <li>Nie może być popularnym hasłem</li>
                    </Typography>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 2, width: '100%' }}>
                        {error}
                    </Alert>
                )}

                <Box component="form" noValidate onSubmit={handleSubmit} sx={{ width: '100%' }}>
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="new-password"
                        label="Nowe hasło"
                        type="password"
                        autoComplete="new-password"
                        value={newPassword}
                        onChange={e => setNewPassword(e.target.value)}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="confirm-password"
                        label="Potwierdź hasło"
                        type="password"
                        autoComplete="new-password"
                        value={confirmPassword}
                        onChange={e => setConfirmPassword(e.target.value)}
                        error={Boolean(error)}
                        sx={{ mb: 3 }}
                    />
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={{
                            mt: 1,
                            mb: 2,
                            padding: '12px',
                            backgroundColor: '#E31837',
                            '&:hover': {
                                backgroundColor: '#FF1F3F',
                            },
                        }}
                    >
                        Ustaw hasło
                    </Button>
                </Box>

                <MuiLink
                    onClick={() => navigate('/login')}
                    sx={{
                        color: '#E31837',
                        textDecoration: 'none',
                        cursor: 'pointer',
                        '&:hover': {
                            textDecoration: 'underline',
                        },
                    }}
                >
                    Powrót do logowania
                </MuiLink>
            </Box>
        </Box>
    );
};

export default SetPassword;
